import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import ClockTimePicker from './ClockTimePicker';

/**
 * Date + Time picker that emits an ISO-ish datetime string (YYYY-MM-DD HH:mm).
 * Mirrors mobile UX: a calendar dropdown for the date, a time slot select for the time.
 *
 * Props:
 *  - value: current "YYYY-MM-DD HH:mm" string (or '' when unset)
 *  - onChange: (newValue) => void  - receives the combined date+time string
 *  - hasError: bool to apply error styling
 *  - minDate: optional Date - earliest allowed date (default: today)
 */
const DateTimePickerField = ({ value, onChange, hasError = false, minDate }) => {
  const initialDate = value ? parseValueDate(value) : null;
  const initialTime = value ? parseValueTime(value) : '';

  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [open, setOpen] = useState(false);

  const today = minDate || new Date();
  const minDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const emit = (nextDate, nextTime) => {
    if (!nextDate || !nextTime) return;
    const dateStr = format(nextDate, 'yyyy-MM-dd');
    onChange(`${dateStr} ${nextTime}`);
  };

  const handleDateSelect = (selected) => {
    if (!selected) return;
    setDate(selected);
    setOpen(false);
    if (time) emit(selected, time);
  };

  const handleTimeChange = (next) => {
    setTime(next);
    if (date) emit(date, next);
  };

  const displayDate = date ? format(date, 'EEE, MMM d, yyyy') : 'Pick a date';

  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={`w-full justify-start text-left font-normal h-12 rounded-xl bg-black/30 border-white/10 text-white hover:bg-black/40 hover:text-white px-4 ${
              hasError ? 'border-red-500' : ''
            } ${!date ? 'text-gray-500' : ''}`}
            data-testid="transport-date-trigger"
          >
            <CalendarIcon className="mr-3 h-5 w-5 text-amber-500" />
            {displayDate}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-[#1a1a1a] border-white/10 z-[80]"
          align="start"
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            disabled={(d) => d < minDateOnly}
            initialFocus
            className="bg-[#1a1a1a] text-white [&_button]:text-white [&_.rdp-day_selected]:bg-amber-500 [&_.rdp-day_selected]:text-black [&_.rdp-day_today]:text-amber-500"
          />
        </PopoverContent>
      </Popover>

      <ClockTimePicker
        value={time}
        onChange={handleTimeChange}
        hasError={hasError}
      />
    </div>
  );
};

const parseValueDate = (s) => {
  const datePart = s.split(' ')[0];
  if (!datePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const parseValueTime = (s) => {
  const parts = s.split(' ');
  return parts.length > 1 ? parts[1] : '';
};

export default DateTimePickerField;
